import { CdkDragDrop } from "@angular/cdk/drag-drop";
import { Component, OnInit } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { ActivatedRoute } from "@angular/router";
import { filter, map, tap } from "rxjs/operators";
import { ConfirmationDialogComponent } from "../general/confirmationDialog.component";
import { TaxonomyService, TenantService } from "../services";
import { HasChanges } from "../services/HasChanges";
import { AddCategoryDialogComponent } from "./addCategoryDialog.component";

@Component({
    styles: [`
        .action-parent .action {
            visibility: hidden;
        }

        .action-parent:hover .action {
            visibility: visible;
        }

        mat-list-item {
            cursor: move;
        }
    `],
    template: `
        <mat-toolbar>
            <span fxFlex></span>
            <button mat-raised-button color="primary" (click)="addCategory()" style="margin-right: 15px;">Add Category</button>
            <button mat-raised-button color="primary" (click)="save()" [disabled]="!isChanged">Save</button>
        </mat-toolbar>
        <mat-list style="margin: 20px;" cdkDropList (cdkDropListDropped)="drop($event)">
            <ng-container *ngFor="let cat of taxonomy">
                <mat-list-item class="action-parent" cdkDrag [cdkDragData]="cat">
                    <span *ngIf="editingCategory !== cat">{{cat.title}}</span>
                    <mat-form-field *ngIf="editingCategory === cat">
                        <input matInput [(ngModel)]="editValue">
                        <button mat-icon-button matSuffix (click)="updateCategory()">
                            <mat-icon>done</mat-icon>
                        </button>
                        <button mat-icon-button matSuffix (click)="cancelUpdate()">
                            <mat-icon>close</mat-icon>
                        </button>
                    </mat-form-field>
                    <button *ngIf="!!cat.removable && editingCategory !== cat" mat-icon-button (click)="editCategory(cat)" class="action" matTooltip="Edit">
                        <mat-icon>mode_edit</mat-icon>
                    </button>
                    <button *ngIf="!!cat.removable && editingCategory !== cat" mat-icon-button (click)="removeFromCategories(cat, taxonomy)" class="action" matTooltip="Remove">
                        <mat-icon>close</mat-icon>
                    </button>
                </mat-list-item>
                <ng-container *ngFor="let subcat of cat.subcategories">
                    <mat-list-item style="margin-left: 30px;" class="action-parent" cdkDrag [cdkDragData]="subcat">
                        <span *ngIf="editingCategory !== subcat">{{subcat.title}}</span>
                        <mat-form-field *ngIf="editingCategory === subcat">
                            <input ngInput [(ngModel)]="editValue">
                            <button mat-icon-button matSuffix (click)="updateCategory()">
                                <mat-icon>done</mat-icon>
                            </button>
                            <button mat-icon-button matSuffix (click)="cancelUpdate()">
                                <mat-icon>close</mat-icon>
                            </button>
                        </mat-form-field>
                        <button *ngIf="!!subcat.removable" mat-icon-button (click)="editCategory(subcat)" class="action" matTooltip="Edit">
                            <mat-icon>mode_edit</mat-icon>
                        </button>
                        <button mat-icon-button *ngIf="!!subcat.removable" (click)="removeFromCategories(subcat, cat.subcategories)" class="action" matTooltip="Remove">
                            <mat-icon>close</mat-icon>
                        </button>
                    </mat-list-item>
                </ng-container>        
            </ng-container>
        </mat-list>
    `
})
export class CategoryManagementComponent implements OnInit, HasChanges {
    taxonomy: any;
    isChanged: boolean;
    catalog: any;
    serializedTaxonomy: string;
    editingCategory;
    editValue;
    isCatalog: any;
    taxonomyVersion: any;

    constructor(private route: ActivatedRoute,
                private dialog: MatDialog, 
                private snackbar: MatSnackBar,
                private taxonomyService: TaxonomyService,
                private tenantService: TenantService) {}
    
    ngOnInit(): void {
        this.isCatalog = this.route.snapshot.data.isCatalog;
        if (this.isCatalog) {
            this.tenantService.getCatalog(this.catalogId).subscribe(catalog => this.catalog = catalog);
            this.taxonomyService.getTenantlessCatalogTaxonomy(this.catalogId, { filterEmpty: false})
            .pipe(
                tap(taxonomy => this.serializedTaxonomy = JSON.stringify(taxonomy))
            ).subscribe(taxonomy => this.taxonomy = taxonomy);
        } else {
            this.fetchTenantTaxonomy();
        }
    }

    fetchTenantTaxonomy(): void {
        this.taxonomyService.getTenantTaxonomyDetails(false, false, true)
        .pipe(
            tap(results => this.taxonomyVersion = results.version),
            map(results => results.taxonomy),
            tap(taxonomy => this.serializedTaxonomy = JSON.stringify(taxonomy))
        ).subscribe(taxonomy => this.taxonomy = taxonomy);
    }

    get catalogId(): string {
        return this.route.snapshot.parent.params.catalogId;
    }

    removeFromCategories(cat, categories): void {
        this.dialog.open(ConfirmationDialogComponent, { 
            data: {
                title: `Delete ${cat.title}?`, 
                actionLabel: 'Delete'}
            }).afterClosed().pipe(
                filter(doIt => doIt),
                map(() => categories.findIndex(category => category.id === cat.id)),
                tap(index => categories.splice(index, 1))
            ).subscribe(() => this.isChanged = true);
    }

    indexMap() {
        const map = [];
        this.taxonomy.forEach(category => {
            map.push({ parent: this.taxonomy, category });
            category.subcategories?.forEach(subcat => map.push({ 
                parent: category.subcategories, 
                category: subcat, 
                isSubcategory: true 
            }));
        });
        return map;
    }

    removeFromParent(target): void {
        target.parent.splice(target.parent.indexOf(target.category), 1);
    }

    drop(event: CdkDragDrop<any>) {
        const indexMap = this.indexMap();
        
        const target = indexMap[event.previousIndex];
        if (event.currentIndex === 0) {
            this.removeFromParent(target);
            this.taxonomy.unshift(target.category);
        } else {
            const dropParent = indexMap.reduceRight((res, el, index) => res || (!el.isSubcategory && index < event.currentIndex ? el : undefined), undefined);

            if (event.distance.x > 100) {
                const targetIndex = event.currentIndex - indexMap.indexOf(dropParent) - 1;
                this.removeFromParent(target);
                dropParent.category.subcategories.splice(targetIndex, 0, target.category);
            } else {
                this.removeFromParent(target);
                this.taxonomy.splice((this.taxonomy.indexOf(dropParent.category) + 1), 0, target.category);
            }
        }
        this.isChanged = true;
    }

    addCategory(): void {
        this.dialog.open(AddCategoryDialogComponent).afterClosed().pipe(
            filter(res => !!res),
            tap(() => this.isChanged = true),
        ).subscribe(cat => this.taxonomy.unshift(cat));
    }

    save(): void {
        const obs = this.isCatalog ?
                    this.taxonomyService.putCatalogTaxonomy(this.catalogId, this.taxonomy) :
                    this.taxonomyService.putTenantTaxonomy(this.taxonomy, this.taxonomyVersion);        
        obs.pipe(
                tap(() => this.isChanged = false),
                tap(() => {
                    if (!this.isCatalog) {
                        this.fetchTenantTaxonomy();
                    }
                }),
                tap(() => this.serializedTaxonomy = JSON.stringify(this.taxonomy))
            ).subscribe(() => this.snackbar.open(`Categories saved.`, 'OK', { duration: 5000 }));
    }

    editCategory(cat): void {
        this.editingCategory = cat;
        this.editValue = cat.title;
    }

    updateCategory(): void {
        this.editingCategory.title = this.editValue;
        this.isChanged = true;
        this.editingCategory = undefined;
    }

    cancelUpdate(): void {
        this.editingCategory = undefined;
    }

    hasChanges(): boolean {
        return JSON.stringify(this.taxonomy) !== this.serializedTaxonomy;
    }

    existingChangesMessage(): string {
        if (this.isCatalog) {       
            return `The changes to the categories for ${this.catalog.title} will be lost unless saved. Continue?`;
        } else {
            return `The changes to the categories will be lost unless saved. Continue?`;
        }
    }
}